import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GetStartedForm } from '../components/GetStartedForm';
import { VideoModal } from '../components/VideoModal';
import { MessageSquare, Code, Workflow, Zap, CheckCircle, HelpCircle } from 'lucide-react';
export function HowItWorksPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-661">
      <Header data-id="element-662" />
      <main className="flex-grow" data-id="element-663">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-664">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-665">
            <div className="text-center" data-id="element-666">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-667">
                How Agentter.ai Works
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-300" data-id="element-668">
                A step-by-step guide to creating powerful AI agents that
                transform your business operations.
              </p>
            </div>
          </div>
        </div>
        {/* Process Steps */}
        <div className="py-16 md:py-24 bg-white" data-id="element-669">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-670">
            <div className="max-w-3xl mx-auto" data-id="element-671">
              <ProcessStep number="01" icon={<MessageSquare size={24} data-id="element-673" />} title="Describe Your Needs" description="Tell us what you need in plain English. No technical jargon required. Simply describe the tasks you want to automate." image="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" data-id="element-672" />
              <ProcessStep number="02" icon={<Code size={24} data-id="element-675" />} title="Connect Your Systems" description="Integrate with your existing software through our simple connection interface. We support hundreds of popular business tools." image="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" data-id="element-674" />
              <ProcessStep number="03" icon={<Workflow size={24} data-id="element-677" />} title="Configure Your Agent" description="Fine-tune your agent's behavior, set permissions, and define the scope of its actions. You maintain full control." image="https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" data-id="element-676" />
              <ProcessStep number="04" icon={<Zap size={24} data-id="element-679" />} title="Activate and Optimize" description="Launch your agent and monitor its performance. Our platform provides insights to help you optimize over time." image="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" isLast={true} data-id="element-678" />
            </div>
          </div>
        </div>
        {/* Demo Video Section */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-680">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-681">
            <div className="text-center mb-12" data-id="element-682">
              <h2 className="text-3xl font-bold mb-4" data-id="element-683">
                See Agentter.ai in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-684">
                Watch how easy it is to create and deploy an AI agent in just a
                few minutes.
              </p>
            </div>
            <div className="max-w-4xl mx-auto bg-white p-4 rounded-xl shadow-lg" data-id="element-685">
              <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer" onClick={() => setIsVideoOpen(true)} data-id="element-686">
                <div className="text-center p-12" data-id="element-687">
                  <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-688">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-689">
                      <polygon points="5 3 19 12 5 21 5 3" data-id="element-690"></polygon>
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium" data-id="element-691">
                    Demo Video: Creating Your First AI Agent
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Benefits Section */}
        <div className="py-16 md:py-24 bg-white" data-id="element-692">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-693">
            <div className="text-center mb-12" data-id="element-694">
              <h2 className="text-3xl font-bold mb-4" data-id="element-695">
                Benefits of Our Approach
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-696">
                Why businesses choose Agentter.ai for their automation needs.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-697">
              <BenefitCard title="No Coding Required" description="Create sophisticated agents using natural language, without writing a single line of code." data-id="element-698" />
              <BenefitCard title="Rapid Deployment" description="Go from idea to working agent in minutes instead of weeks or months of development." data-id="element-699" />
              <BenefitCard title="Enterprise Security" description="Bank-level security with SOC 2 compliance and end-to-end encryption for all your data." data-id="element-700" />
              <BenefitCard title="Continuous Learning" description="Agents improve over time based on feedback and changing business requirements." data-id="element-701" />
              <BenefitCard title="Seamless Integration" description="Connect to hundreds of popular business tools and software with pre-built integrations." data-id="element-702" />
              <BenefitCard title="Full Transparency" description="Complete visibility into how your agents make decisions and handle tasks." data-id="element-703" />
            </div>
          </div>
        </div>
        {/* FAQ Section */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-704">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-705">
            <div className="text-center mb-12" data-id="element-706">
              <h2 className="text-3xl font-bold mb-4" data-id="element-707">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-708">
                Common questions about our platform and technology.
              </p>
            </div>
            <div className="max-w-3xl mx-auto" data-id="element-709">
              <FAQItem question="What kind of tasks can Agentter.ai automate?" answer="Agentter.ai can automate a wide range of tasks including customer service inquiries, data entry, appointment scheduling, document processing, inventory management, and much more. If a task follows a predictable pattern, our agents can likely automate it." data-id="element-710" />
              <FAQItem question="Do I need technical knowledge to use Agentter.ai?" answer="No, our platform is designed to be accessible to non-technical users. You describe what you need in plain English, and our system handles the technical implementation. No coding or AI expertise is required." data-id="element-711" />
              <FAQItem question="How secure is my business data with Agentter.ai?" answer="We take security extremely seriously. All data is encrypted both in transit and at rest. We're SOC 2 compliant and follow industry best practices for data security and privacy. You maintain full control over what data your agents can access." data-id="element-712" />
              <FAQItem question="Can I customize my agents after they're deployed?" answer="Absolutely. You can adjust your agents' behavior, add new capabilities, or modify existing ones at any time. Our platform makes it easy to evolve your agents as your business needs change." data-id="element-713" />
              <FAQItem question="What software can Agentter.ai integrate with?" answer="We offer pre-built integrations with hundreds of popular business tools including CRM systems, email platforms, project management software, e-commerce platforms, and more. We also offer an API for custom integrations." data-id="element-714" />
              <FAQItem question="How long does it take to deploy an agent?" answer="Most agents can be deployed in minutes to hours, depending on complexity. Simple agents can be up and running in as little as 15 minutes, while more complex workflows might take a few hours to configure properly." data-id="element-715" />
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-716">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-717">
            <h2 className="text-3xl font-bold mb-6" data-id="element-718">Ready to get started?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-719">
              Create your first AI agent today and experience the power of
              intelligent automation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-720">
              <button onClick={() => setIsFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-721">
                Start Free Trial
              </button>
              <button onClick={() => setIsVideoOpen(true)} className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-722">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-723" />
      {/* Modals */}
      <GetStartedForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} data-id="element-724" />
      <VideoModal isOpen={isVideoOpen} onClose={() => setIsVideoOpen(false)} data-id="element-725" />
    </div>;
}
function ProcessStep({
  number,
  icon,
  title,
  description,
  image,
  isLast = false
}) {
  return <div className="mb-16 last:mb-0" data-id="element-726">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6" data-id="element-727">
        <div className="bg-black text-white text-2xl font-bold w-14 h-14 rounded-full flex items-center justify-center shrink-0" data-id="element-728">
          {number}
        </div>
        <div data-id="element-729">
          <div className="flex items-center mb-2" data-id="element-730">
            <div className="bg-gray-100 p-2 rounded-full mr-3" data-id="element-731">{icon}</div>
            <h3 className="text-2xl font-bold" data-id="element-732">{title}</h3>
          </div>
          <p className="text-gray-600" data-id="element-733">{description}</p>
        </div>
      </div>
      <div className="ml-7 pl-7 border-l-2 border-gray-200" data-id="element-734">
        <img src={image} alt={title} className="rounded-lg w-full h-64 object-cover" data-id="element-735" />
      </div>
      {!isLast && <div className="h-16 ml-7 border-l-2 border-gray-200" data-id="element-736"></div>}
    </div>;
}
function BenefitCard({
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100" data-id="element-737">
      <div className="mb-4" data-id="element-738">
        <CheckCircle className="text-black" size={24} data-id="element-739" />
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-740">{title}</h3>
      <p className="text-gray-600" data-id="element-741">{description}</p>
    </div>;
}
function FAQItem({
  question,
  answer
}) {
  return <div className="mb-6 last:mb-0 border-b border-gray-200 pb-6 last:border-0" data-id="element-742">
      <div className="flex items-start mb-2" data-id="element-743">
        <HelpCircle className="text-black mr-3 mt-1 shrink-0" size={20} data-id="element-744" />
        <h3 className="text-xl font-semibold" data-id="element-745">{question}</h3>
      </div>
      <p className="text-gray-600 ml-8" data-id="element-746">{answer}</p>
    </div>;
}